import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  inventoryCounts,
  type InventoryCountItem,
} from '@/api/inventoryCounts';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  inventory_date: string;
  comment: string;
  items: InventoryCountItem[];
}

const today = new Date().toISOString().slice(0, 10);

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  inventory_date: today,
  comment: '',
  items: [],
};

const newRow = (n: number): InventoryCountItem => ({
  line_number: n,
  nomenclature_id: '',
  fact_quantity: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
const fmtMoney = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InventoryCountEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.inventory.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.inventory.manage');
  const docQuery = useQuery({
    queryKey: ['inventory-count', id],
    queryFn: () => inventoryCounts.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'inventory-count', (cid) => inventoryCounts.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.inventory_counts', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        inventory_date: d.inventory_date.slice(0, 10),
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          book_quantity: it.book_quantity,
          fact_quantity: it.fact_quantity,
          variance_quantity: it.variance_quantity,
          book_amount: it.book_amount,
          fact_amount: it.fact_amount,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const create = useMutation({
    mutationFn: () => inventoryCounts.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id,
      inventory_date: form.inventory_date,
      comment: form.comment || null,
      items: form.items.filter((it) => it.nomenclature_id),
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate(`/inventory-counts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => inventoryCounts.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id,
      inventory_date: form.inventory_date,
      comment: form.comment || null,
      items: form.items.filter((it) => it.nomenclature_id),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-count', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // 2026-08-11 (рішення власника): «заповнити» = створити рядки з УСІХ
  // залишків складу; статусний цикл DRAFT → IN_PROGRESS → POSTED.
  const fill = useMutation({
    mutationFn: () => inventoryCounts.fillFromStock(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-count', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // 2026-08-11 (зауваження власника «кнопки нет на /new»): на новому
  // документі кнопка САМА зберігає і одразу заповнює з залишків.
  const createAndFill = useMutation({
    mutationFn: async () => {
      const doc = await inventoryCounts.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        inventory_date: form.inventory_date,
        comment: form.comment || null,
        items: [],
      });
      await inventoryCounts.fillFromStock(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate(`/inventory-counts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await inventoryCounts.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        inventory_date: form.inventory_date,
        comment: form.comment || null,
        items: form.items.filter((it) => it.nomenclature_id),
      });
      try {
        await inventoryCounts.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/inventory-counts/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate(`/inventory-counts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await inventoryCounts.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        inventory_date: form.inventory_date,
        comment: form.comment || null,
        items: form.items.filter((it) => it.nomenclature_id),
      });
      await inventoryCounts.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-count', id] });
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => inventoryCounts.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-count', id] });
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => inventoryCounts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate('/inventory-counts');
    },
  });

  function setItem(i: number, patch: Partial<InventoryCountItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.inventory.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати інвентаризацію у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/inventory-counts')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.886 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) + (form.inventory_date ? 1 : 0) },
    { id: 'wh', title: 'Склад', required: 1, filled: form.warehouse_id ? 1 : 0 },
    { id: 'items', title: 'Рядки', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова інвентаризація' : `Інвентаризація: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            entriesDoc={{ type: 'doc.inventory_counts', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
            extraStatusBadges={!isPosted && docQuery.data?.state === 'IN_PROGRESS' ? [{
              label: 'в процесі', tone: 'amber',
              title: 'Опис у комісії — триває перерахунок. Внесіть фактичні кількості з підписаного акта і проведіть.',
            }] : []}
            onBackToList={() => navigate('/inventory-counts')}
            onSave={() => isNew ? create.mutate() : update.mutate()}
            saveDisabled={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            postDisabled={!isNew && form.items.length === 0}
            onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
            postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
            postTitle="Зберігає фактичні кількості, списує недостачі та оприбутковує надлишки; статус — «виконано»"
            postLabel="закрити та провести"
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
            deletePending={remove.isPending}
            onPrint={() => navigate(`/inventory-counts/${id}/print`)}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/inventory-counts/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.inventory_counts', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата документа *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата інвентаризації *</label>
          <DatePopover
            value={form.inventory_date}
            onChange={(v) => setForm({ ...form, inventory_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>

      </DocBlock>

      {/* Склад + «заповнити з залишків» (зауваження власника) — ч.886: кнопка у
          заголовку блоку, `.btn-doc` з іконкою. */}
      <DocBlock index={2} title="Склад"
        actions={!isPosted ? (
          <button type="button" className="btn-doc"
            onClick={() => (isNew ? createAndFill.mutate() : fill.mutate())}
            disabled={fill.isPending || createAndFill.isPending || !form.warehouse_id || !form.organization_id}
            title={isNew
              ? 'Зберігає документ і створює рядки опису з УСІХ залишків складу на дату (факт = книжковий)'
              : 'Створити рядки опису з УСІХ залишків складу на дату (факт = книжковий; наявні рядки замінюються)'}>
            <DocIcon />заповнити з залишків
          </button>
        ) : undefined}>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Склад *</label>
          <select
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
            ))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Рядки"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={addRow}>+ додати рядок</button>
        ) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 110, textAlign: 'right' }}>Облік (книжно)</th>
            <th style={{ width: 130, textAlign: 'right' }}>Факт</th>
            <th style={{ width: 110, textAlign: 'right' }}>Відхилення</th>
            <th style={{ width: 130, textAlign: 'right' }}>Сума обліку</th>
            {isPosted && <th style={{ width: 130, textAlign: 'right' }}>Сума факту</th>}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const variance = Number(it.fact_quantity ?? 0) - Number(it.book_quantity ?? 0);
            const varBg = variance > 0 ? '#dcfce7' : variance < 0 ? '#fee2e2' : undefined;
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td>
                  <NomenclatureSelect
                    value={it.nomenclature_id}
                    disabled={isPosted}
                    onChange={(v) => setItem(i, { nomenclature_id: v })}
                  />
                </td>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {fmt(Number(it.book_quantity ?? 0))}
                </td>
                <td>
                  <input
                    type="number" step="0.001" value={it.fact_quantity}
                    onChange={(e) => setItem(i, { fact_quantity: e.target.value })}
                    disabled={isPosted}
                    style={{ textAlign: 'right' }}
                  />
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600, background: varBg }}>
                  {variance > 0 ? '+' : ''}{fmt(variance)}
                </td>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {fmtMoney(Number(it.book_amount ?? 0))}
                </td>
                {isPosted && (
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {fmtMoney(Number(it.fact_amount ?? 0))}
                  </td>
                )}
                <td>
                  {!isPosted && (
                    <button
                      onClick={() => removeRow(i)}
                      className="danger"
                      style={{ padding: '0.3rem 0.6rem' }}
                    >×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </TchTable>
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.inventory_counts" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          <strong>Як працювати:</strong> 1) Збережіть документ. 2) Натисніть
          «Заповнити з обліку» — система проставить облікове кол-во з регістрів
          на дату інвентаризації. 3) Внесіть фактичні залишки у колонку «Факт».
          4) Натисніть «провести» — для лишків створиться партія
          (з середньозваженою ціною на дату; якщо номенклатури на складі не було —
          0, бухгалтер потім скоригує) і проводка <strong>Дт 281 — Кт 719</strong>;
          для нестач — списання партій за методом організації (FIFO/WAVG)
          і проводка <strong>Дт 947 — Кт 281</strong>.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
